import { Alert, Box, CircularProgress, IconButton, Paper, TextField, Typography } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/button/Button';
import AutocompleteComponent from '../../../../components/layout/Autocomplete';
import { useLazyGetCitiesQuery, useGetProvincesQuery } from '../../../../services/companyApi';
import {
    useCreateSrtFormularioMutation,
    useExportSrtFormularioArchivoMutation,
    useGetSrtFormularioQuery,
    usePatchSrtFormularioMutation
} from '../../../../services/roamApi';
import { downloadFile } from '../../../../utils/common';
import FormSRTSectionCard from './components/FormSRTSectionCard';
import { hasValue, useFormSRT } from './hooks/useFormSRT';
import { buildCreateSrtPayload, mapSrtFormularioToValues } from './utils/srtPayload';
import { esFormularioSrtCompleto, OPTIONAL_REPEATABLE_SECTION_IDS } from './utils/formularioCompleto';
import { useDescargaPdfInvestigacion } from './hooks/useDescargaPdfInvestigacion';
import { SrtBaseFieldConfig, SrtDividerConfig, SrtFieldConfig, SrtRepeatableConfig } from './types';
import { SrtFieldRenderer, SrtRepeatableRenderer } from './components/SrtFieldRenderer';
import { srtStyles } from './styles';

export interface FormSRTOverrides {
    skipInternalQueries?: boolean;
    formularioDataOverride?: any;
    parametricDataOverride?: any;
    onSaveOverride?: (body: any) => Promise<void>;
    isSaving?: boolean;
    readOnly?: boolean;
    hideExport?: boolean;
    // Supervisor (SMP-460): consulta en solo lectura, sin Generar TXT, pero descarga el informe
    // de investigación si el formulario está completo.
    ocultarTxt?: boolean;
    pdfEnSoloLectura?: boolean;
    // Preventor (Visitas): los campos autocompletados de Datos del empleador (02), ART/EA (03) y
    // Centro de Trabajo (subsección de 04) se bloquean apenas llegan con dato — si vienen vacíos,
    // se pueden seguir completando a mano.
    lockAutocompletedFields?: boolean;
}

// Campos de las secciones 02/03 y de la subsección "Centro de Trabajo" (04) que tienen lógica de
// autocompletado (ver useFormSRT: applyEmployerContractDefaults, applyArtEaContractDefaults,
// applyEmployerDefaultsToWorkCenter). `cpaEmpleador`/`domicilioEmpleador`... los de Provincia/
// Ciudad/CP se manejan aparte (son AutocompleteComponent/TextField manuales, no van por acá).
// `denominacionArtCentro`/`codigoArtCentro` no están: ya son siempre readOnly por config de campo.
const AUTOCOMPLETED_LOCKABLE_FIELD_NAMES = new Set([
    'razonSocialEmpleador',
    'cuitDatosEmpleador',
    'telefonoEmpleador',
    'domicilioEmpleador',
    'codigoCiiuEmpleador',
    'denominacionArtEa',
    'codigoArtEa',
    'razonSocialCentro',
    'cuitCentro',
    'codigoCiiuCentro'
]);

interface FormSRTProps {
    siniestro?: any;
    overrides?: FormSRTOverrides;
}

const isRepeatableField = (field: SrtFieldConfig): field is SrtRepeatableConfig =>
    field.type === 'repeatable';

const isDividerField = (field: SrtFieldConfig): field is SrtDividerConfig =>
    field.type === 'divider';

interface ProvinceOption {
    id: number;
    nombre: string;
}

interface CityOption {
    id: number;
    nombre: string;
    codigoPostal?: number;
}

const WORKER_ADDRESS_FIELD_NAMES = new Set([
    'ciudadTrabajador',
    'provinciaTrabajador',
    'codigoPostalTrabajador'
]);
const EMPLOYER_ADDRESS_FIELD_NAMES = new Set([
    'provinciaEmpleador',
    'ciudadEmpleador',
    'cpaEmpleador'
]);
const PLACE_ADDRESS_FIELD_NAMES = new Set([
    'provinciaLugar',
    'ciudadLugar',
    'cpaLugar',
    'lugarCPA'
]);
const CONDITIONAL_ONLY_FIELD_NAMES = new Set(['otrosFiscalizacion']);
const OTHER_NATIONALITY_VALUE = '7';
const OTHER_FISCALIZATION_VALUE = '7';
const OTHER_NATIONALITY_FIELD: SrtBaseFieldConfig = {
    name: 'otrosNacionalidad',
    label: '',
    type: 'text',
    gridColumns: 4
};

const getErrorMessage = (error: any, t: (key: string) => string) => {
    const message = error?.data?.message;

    if (Array.isArray(message)) {
        return message.join('\n');
    }

    if (typeof message === 'string' && message.trim()) {
        return message;
    }

    return t('detail.srtForm.alerts.saveError');
};

const getExtensionFromPresignedUrl = (presignedUrl: string) => {
    try {
        const { pathname } = new URL(presignedUrl);
        const match = pathname.match(/\.([a-z0-9]+)$/i);

        return match ? match[1].toLowerCase() : 'zip';
    } catch {
        return 'zip';
    }
};

const getInitialWorkerProvince = (siniestro?: any): ProvinceOption | null => {
    const provincia = siniestro?.accidentado?.datosContacto?.localidad?.provincia;

    if (!provincia?.id) return null;

    return {
        id: Number(provincia.id),
        nombre: provincia.nombre || ''
    };
};

const getInitialWorkerCity = (siniestro?: any): CityOption | null => {
    const localidad = siniestro?.accidentado?.datosContacto?.localidad;

    if (!localidad?.id) return null;

    return {
        id: Number(localidad.id),
        nombre: localidad.nombre || '',
        codigoPostal: localidad.codigoPostal
    };
};

// Igual que getInitialWorkerProvince/City: mientras no haya un Formulario SRT guardado (que pisaría
// esto con getPlaceProvinceFromFormulario/City), Provincia/Ciudad de "Lugar del accidente" tienen
// que arrancar preseleccionadas con la localidad de lugarOcurrencia que ya viene en el siniestro.
const getInitialPlaceProvince = (siniestro?: any): ProvinceOption | null => {
    const provincia = siniestro?.lugarOcurrencia?.localidad?.provincia;

    if (!provincia?.id) return null;

    return {
        id: Number(provincia.id),
        nombre: provincia.nombre || ''
    };
};

const getInitialPlaceCity = (siniestro?: any): CityOption | null => {
    const localidad = siniestro?.lugarOcurrencia?.localidad;

    if (!localidad?.id) return null;

    return {
        id: Number(localidad.id),
        nombre: localidad.nombre || '',
        codigoPostal: localidad.codigoPostal
    };
};

const getWorkerProvinceFromFormulario = (formulario?: any): ProvinceOption | null => {
    const provincia = formulario?.trabajadorLocalidad?.provincia;

    if (!provincia?.id) return null;

    return {
        id: Number(provincia.id),
        nombre: provincia.nombre || ''
    };
};

const getWorkerCityFromFormulario = (formulario?: any): CityOption | null => {
    const localidad = formulario?.trabajadorLocalidad;

    if (!localidad?.id) return null;

    return {
        id: Number(localidad.id),
        nombre: localidad.nombre || '',
        codigoPostal: localidad.codigoPostal
    };
};

const getInitialEmployerProvince = (contractData?: any): ProvinceOption | null => {
    const provincia = contractData?.direccion?.localidad?.provincia;

    if (!provincia?.id) return null;

    return {
        id: Number(provincia.id),
        nombre: provincia.nombre || ''
    };
};

const getInitialEmployerCity = (contractData?: any): CityOption | null => {
    const localidad = contractData?.direccion?.localidad;

    if (!localidad?.id) return null;

    return {
        id: Number(localidad.id),
        nombre: localidad.nombre || '',
        codigoPostal: localidad.codigoPostal
    };
};

const getEmployerProvinceFromFormulario = (formulario?: any): ProvinceOption | null => {
    const provincia = formulario?.empleadorLocalidad?.provincia;

    if (!provincia?.id) return null;

    return {
        id: Number(provincia.id),
        nombre: provincia.nombre || ''
    };
};

const getEmployerCityFromFormulario = (formulario?: any): CityOption | null => {
    const localidad = formulario?.empleadorLocalidad;

    if (!localidad?.id) return null;

    return {
        id: Number(localidad.id),
        nombre: localidad.nombre || '',
        codigoPostal: localidad.codigoPostal
    };
};

const getPlaceProvinceFromFormulario = (formulario?: any): ProvinceOption | null => {
    const provincia = formulario?.lugarLocalidad?.provincia;

    if (!provincia?.id) return null;

    return {
        id: Number(provincia.id),
        nombre: provincia.nombre || ''
    };
};

const getPlaceCityFromFormulario = (formulario?: any): CityOption | null => {
    const localidad = formulario?.lugarLocalidad;

    if (!localidad?.id) return null;

    return {
        id: Number(localidad.id),
        nombre: localidad.nombre || '',
        codigoPostal: localidad.codigoPostal
    };
};

const getPlaceCPAFromFormulario = (formulario?: any) =>
    formulario?.lugarCPA || formulario?.lugar?.cpa || '';

const renderFixedLengthHelper = (
    value: any,
    maxLength: number,
    error?: string
) => {
    const characterCount = String(value ?? '').length;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {error && (
                <Typography variant="caption" color="error">
                    {error}
                </Typography>
            )}
            <Typography
                variant="caption"
                sx={{
                    color: error ? 'error.main' : 'text.secondary',
                    alignSelf: 'flex-end'
                }}
            >
                {characterCount}/{maxLength}
            </Typography>
        </Box>
    );
};
const getInitialFormularioId = (siniestro?: any) =>
    Number(
        siniestro?.accidenteLaboral?.id ||
            siniestro?.idSRT ||
            siniestro?.idSrt ||
            siniestro?.srt?.id ||
            siniestro?.formularioSRT?.id ||
            0
    ) || null;

const FormSRT = ({ siniestro, overrides }: FormSRTProps) => {
    const { t } = useTranslation('siniestrosRoam');
    const formRef = useRef<HTMLDivElement | null>(null);
    const hasInitializedOpenSectionsRef = useRef(false);
    const hasShownBootstrapErrorRef = useRef(false);
    const hydratedSrtFormularioDataRef = useRef<any>(null);
    const [isFormHydrated, setIsFormHydrated] = useState(!getInitialFormularioId(siniestro));
    const {
        sections,
        constructionSiteFields,
        isConstructionSiteSelected,
        srtParametricData,
        employerContractData,
        values,
        errors,
        sectionsStatus,
        sectionsWithContent,
        handleFieldChange,
        resetForm,
        handleRepeatableFieldChange,
        addRepeatableRow,
        removeRepeatableRow,
        handleMedidaFieldChange,
        addMedidaRow,
        removeMedidaRow,
        validateField,
        otherAffectedLookupLoading,
        maxRepeatableRows,
        validateSection
    } = useFormSRT(siniestro, overrides?.parametricDataOverride);
    const { data: provincesList, isLoading: isLoadingProvinces } = useGetProvincesQuery(null);
    const [getCities, { data: citiesList, isLoading: isLoadingCities }] = useLazyGetCitiesQuery();
    const [getEmployerCities, { data: employerCitiesList, isLoading: isLoadingEmployerCities }] =
        useLazyGetCitiesQuery();
    const [getPlaceCities, { data: placeCitiesList, isLoading: isLoadingPlaceCities }] =
        useLazyGetCitiesQuery();
    const [createSrtFormulario, { isLoading: isSaving }] = useCreateSrtFormularioMutation();
    const [patchSrtFormulario, { isLoading: isPatching }] = usePatchSrtFormularioMutation();
    const [exportSrtFormularioArchivo, { isLoading: isExportingSrt }] =
        useExportSrtFormularioArchivoMutation();
    const [selectedWorkerProvince, setSelectedWorkerProvince] = useState<ProvinceOption | null>(
        getInitialWorkerProvince(siniestro)
    );
    const [selectedWorkerCity, setSelectedWorkerCity] = useState<CityOption | null>(
        getInitialWorkerCity(siniestro)
    );
    const [selectedEmployerProvince, setSelectedEmployerProvince] = useState<ProvinceOption | null>(
        null
    );
    const [selectedEmployerCity, setSelectedEmployerCity] = useState<CityOption | null>(null);
    const [selectedPlaceProvince, setSelectedPlaceProvince] = useState<ProvinceOption | null>(
        getInitialPlaceProvince(siniestro)
    );
    const [selectedPlaceCity, setSelectedPlaceCity] = useState<CityOption | null>(
        getInitialPlaceCity(siniestro)
    );
    const [formularioId, setFormularioId] = useState<number | null>(
        getInitialFormularioId(siniestro)
    );
    const skipInternalQueries = Boolean(overrides?.skipInternalQueries);
    const {
        data: fetchedFormularioData,
        isLoading: isLoadingSrtFormulario,
        error: srtFormularioError
    } = useGetSrtFormularioQuery(
        {
            idSiniestro: Number(siniestro?.id),
            idFormulario: Number(formularioId)
        },
        {
            skip: skipInternalQueries || !siniestro?.id || !formularioId
        }
    );
    const srtFormularioData = skipInternalQueries ? overrides?.formularioDataOverride : fetchedFormularioData;
    const initialOpenSections = useMemo(
        () =>
            sections.reduce<Record<number, boolean>>((acc, section) => {
                acc[section.id] = OPTIONAL_REPEATABLE_SECTION_IDS.includes(section.id)
                    ? false
                    : !sectionsStatus[section.id];
                return acc;
            }, {}),
        [sections, sectionsStatus]
    );
    const [openSections, setOpenSections] = useState<Record<number, boolean>>(initialOpenSections);
    const areAllSectionsCollapsed = sections.every((section) => !openSections[section.id]);
    const hasMedidasCorrectivas = useMemo(
        () =>
            (values.medidasCorrectivas || []).some((row: Record<string, any>) =>
                (row?.medidas || []).some(
                    (medida: Record<string, any>) =>
                        typeof medida?.medidaCorrectiva === 'string' &&
                        medida.medidaCorrectiva.trim() !== ''
                )
            ),
        [values.medidasCorrectivas]
    );

    useEffect(() => {
        if (!hasMedidasCorrectivas) {
            setOpenSections((current) => ({ ...current, [11]: false }));
        }
    }, [hasMedidasCorrectivas]);
    const isSavingForm = overrides?.isSaving ?? (isSaving || isPatching);
    const shouldWaitForSrtFormulario = !skipInternalQueries && Boolean(formularioId);
    const isBootstrappingSrtFormulario = shouldWaitForSrtFormulario && isLoadingSrtFormulario;
    const bootstrapErrorMessage = (!skipInternalQueries && srtFormularioError) ? getErrorMessage(srtFormularioError, t) : '';
    const hasBootstrapError = !skipInternalQueries && Boolean(srtFormularioError);
    // TXT e informe de investigacion se habilitan juntos: formulario guardado y completo, sin solo
    // lectura ni error de carga.
    const formularioListoParaExportar =
        Boolean(formularioId) &&
        !hasBootstrapError &&
        esFormularioSrtCompleto(sectionsStatus, sectionsWithContent);
    const puedeExportarInvestigacion = !overrides?.readOnly && formularioListoParaExportar;
    const puedeDescargarPdf =
        puedeExportarInvestigacion ||
        (Boolean(overrides?.pdfEnSoloLectura) && formularioListoParaExportar);
    const { descargandoPdf, handleClickPdf } = useDescargaPdfInvestigacion({
        idSiniestro: siniestro?.id ? Number(siniestro.id) : undefined,
        habilitado: puedeDescargarPdf
    });
    const provinceOptions = provincesList?.data?.provincias || [];
    const workerCityOptions = useMemo(
        () => (selectedWorkerProvince ? citiesList?.data?.localidades || [] : []),
        [citiesList?.data?.localidades, selectedWorkerProvince]
    );
    const employerCityOptions = useMemo(
        () => (selectedEmployerProvince ? employerCitiesList?.data?.localidades || [] : []),
        [employerCitiesList?.data?.localidades, selectedEmployerProvince]
    );
    const placeCityOptions = useMemo(
        () => (selectedPlaceProvince ? placeCitiesList?.data?.localidades || [] : []),
        [placeCitiesList?.data?.localidades, selectedPlaceProvince]
    );

    const resetLocationSelectors = () => {
        setSelectedWorkerProvince(getInitialWorkerProvince(siniestro));
        setSelectedWorkerCity(getInitialWorkerCity(siniestro));
        setSelectedEmployerProvince(null);
        setSelectedEmployerCity(null);
        setSelectedPlaceProvince(getInitialPlaceProvince(siniestro));
        setSelectedPlaceCity(getInitialPlaceCity(siniestro));
    };

    useEffect(() => {
        resetLocationSelectors();
        setFormularioId(getInitialFormularioId(siniestro));
        setIsFormHydrated(!getInitialFormularioId(siniestro));
        hasInitializedOpenSectionsRef.current = false;
        hasShownBootstrapErrorRef.current = false;
    }, [siniestro]);

    // Autocompleta Provincia/Ciudad del empleador con la localidad del contrato la primera vez
    // (mientras no haya nada seleccionado). Si ya existe un Formulario SRT guardado, el efecto
    // de hidratación de srtFormularioData (más abajo) pisa este valor con el dato real persistido.
    useEffect(() => {
        if (!employerContractData) return;
        if (selectedEmployerProvince || selectedEmployerCity) return;

        setSelectedEmployerProvince(getInitialEmployerProvince(employerContractData));
        setSelectedEmployerCity(getInitialEmployerCity(employerContractData));
    }, [employerContractData, selectedEmployerProvince, selectedEmployerCity]);

    useEffect(() => {
        if (
            isBootstrappingSrtFormulario ||
            !isFormHydrated ||
            hasInitializedOpenSectionsRef.current
        ) {
            return;
        }

        setOpenSections(initialOpenSections);
        hasInitializedOpenSectionsRef.current = true;
    }, [initialOpenSections, isBootstrappingSrtFormulario, isFormHydrated]);

    useEffect(() => {
        if (!srtFormularioData) return;
        // `resetForm`/`handleFieldChange` cambian de identidad cuando terminan de resolver
        // datos ajenos a este formulario (contrato del empleador, CIIU, etc. — ver hooks de
        // useFormSRT), y ambos están en las deps de este efecto. Sin este guard, el efecto se
        // reejecuta cada vez que eso pasa aunque `srtFormularioData` no haya cambiado, pisando de
        // nuevo Provincia/Ciudad del empleador (que sí se habían autocompletado bien mientras
        // tanto) y volviendo a correr `resetForm`, que podría tirar abajo ediciones en curso del
        // Preventor. Se hidrata una sola vez por cada `srtFormularioData` real.
        if (hydratedSrtFormularioDataRef.current === srtFormularioData) return;
        hydratedSrtFormularioDataRef.current = srtFormularioData;

        const mappedValues = Object.fromEntries(
            Object.entries(mapSrtFormularioToValues(srtFormularioData)).filter(
                ([, value]) => value !== undefined
            )
        );

        resetForm(mappedValues);
        setSelectedWorkerProvince(getWorkerProvinceFromFormulario(srtFormularioData));
        setSelectedWorkerCity(getWorkerCityFromFormulario(srtFormularioData));
        setSelectedEmployerProvince(getEmployerProvinceFromFormulario(srtFormularioData));
        setSelectedEmployerCity(getEmployerCityFromFormulario(srtFormularioData));
        setSelectedPlaceProvince(getPlaceProvinceFromFormulario(srtFormularioData));
        setSelectedPlaceCity(getPlaceCityFromFormulario(srtFormularioData));
        handleFieldChange('lugarCPA', getPlaceCPAFromFormulario(srtFormularioData));
        setIsFormHydrated(true);
    }, [handleFieldChange, resetForm, srtFormularioData]);

    useEffect(() => {
        if (!srtFormularioError) return;

        setIsFormHydrated(true);

        if (hasShownBootstrapErrorRef.current) return;

        enqueueSnackbar(getErrorMessage(srtFormularioError, t), {
            variant: 'error'
        });
        hasShownBootstrapErrorRef.current = true;
    }, [srtFormularioError, t]);

    useEffect(() => {
        if (!formularioId) {
            setIsFormHydrated(true);
        }
    }, [formularioId]);

    useEffect(() => {
        if (selectedWorkerProvince?.id) {
            getCities({ provinceId: selectedWorkerProvince.id });
        }
    }, [getCities, selectedWorkerProvince?.id]);

    useEffect(() => {
        if (selectedEmployerProvince?.id) {
            getEmployerCities({ provinceId: selectedEmployerProvince.id });
        }
    }, [getEmployerCities, selectedEmployerProvince?.id]);

    useEffect(() => {
        if (selectedPlaceProvince?.id) {
            getPlaceCities({ provinceId: selectedPlaceProvince.id });
        }
    }, [getPlaceCities, selectedPlaceProvince?.id]);

    useEffect(() => {
        if (!selectedWorkerCity?.id || !workerCityOptions.length) return;

        const matchedCity = workerCityOptions.find(
            (city: CityOption) => city.id === selectedWorkerCity.id
        );

        if (!matchedCity) return;

        setSelectedWorkerCity(matchedCity);
        handleFieldChange('idLocalidadTrabajador', matchedCity.id);
        handleFieldChange('ciudadTrabajador', matchedCity.nombre || '');
        handleFieldChange(
            'codigoPostalTrabajador',
            matchedCity.codigoPostal ? String(matchedCity.codigoPostal) : ''
        );
    }, [handleFieldChange, selectedWorkerCity?.id, workerCityOptions]);

    useEffect(() => {
        if (!selectedEmployerCity?.id || !employerCityOptions.length) return;

        const matchedCity = employerCityOptions.find(
            (city: CityOption) => city.id === selectedEmployerCity.id
        );

        if (!matchedCity) return;

        setSelectedEmployerCity(matchedCity);
        handleFieldChange('idLocalidadEmpleador', matchedCity.id);
        handleFieldChange('ciudadEmpleador', matchedCity.nombre || '');
        handleFieldChange(
            'cpaEmpleador',
            matchedCity.codigoPostal ? String(matchedCity.codigoPostal) : ''
        );
    }, [employerCityOptions, handleFieldChange, selectedEmployerCity?.id]);

    useEffect(() => {
        if (!selectedPlaceCity?.id || !placeCityOptions.length) return;

        const matchedCity = placeCityOptions.find(
            (city: CityOption) => city.id === selectedPlaceCity.id
        );

        if (!matchedCity) return;

        setSelectedPlaceCity(matchedCity);
        handleFieldChange('idLocalidadLugar', matchedCity.id);
        handleFieldChange('ciudadLugar', matchedCity.nombre || '');
        handleFieldChange(
            'cpaLugar',
            matchedCity.codigoPostal ? String(matchedCity.codigoPostal) : ''
        );
    }, [handleFieldChange, placeCityOptions, selectedPlaceCity?.id]);

    const handleToggleSection = useCallback((sectionId: number) => {
        setOpenSections((current) => ({
            ...current,
            [sectionId]: !current[sectionId]
        }));
    }, []);

    const setAllSectionsOpenState = (isOpen: boolean) => {
        setOpenSections(
            sections.reduce<Record<number, boolean>>((acc, section) => {
                acc[section.id] = isOpen;
                return acc;
            }, {})
        );
    };

    const handleToggleAllSections = () => {
        setAllSectionsOpenState(areAllSectionsCollapsed);
    };

    const handleScrollToTop = () => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleExportSrt = async () => {
        if (!formularioId) {
            enqueueSnackbar(t('detail.srtForm.alerts.downloadError'), { variant: 'warning' });
            return;
        }

        try {
            const response = await exportSrtFormularioArchivo({
                idsAccidenteLaboral: [Number(formularioId)]
            }).unwrap();

            if (!response?.presignedUrl) {
                throw new Error('Missing presignedUrl');
            }

            const fileResponse = await fetch(response.presignedUrl);
            const fileBlob = await fileResponse.blob();
            const fileExtension = getExtensionFromPresignedUrl(response.presignedUrl);

            downloadFile({
                blob: fileBlob,
                name: `srt_${formularioId}.${fileExtension}`
            });
        } catch (error: any) {
            enqueueSnackbar(
                error?.data?.message || t('detail.srtForm.alerts.downloadError'),
                { variant: 'error' }
            );
        }
    };

    const handleWorkerProvinceChange = (province: ProvinceOption | '' | null) => {
        const nextProvince = province && typeof province === 'object' ? province : null;

        setSelectedWorkerProvince(nextProvince);
        setSelectedWorkerCity(null);
        handleFieldChange('provinciaTrabajador', nextProvince?.nombre || '');
        handleFieldChange('idProvinciaTrabajador', nextProvince?.id || '');
        handleFieldChange('ciudadTrabajador', '');
        handleFieldChange('idLocalidadTrabajador', '');
        handleFieldChange('codigoPostalTrabajador', '');
    };

    const handleWorkerCityChange = (city: CityOption | '' | null) => {
        const nextCity = city && typeof city === 'object' ? city : null;

        setSelectedWorkerCity(nextCity);
        handleFieldChange('idLocalidadTrabajador', nextCity?.id || '');
        handleFieldChange('ciudadTrabajador', nextCity?.nombre || '');
        handleFieldChange(
            'codigoPostalTrabajador',
            nextCity?.codigoPostal ? String(nextCity.codigoPostal) : ''
        );
    };

    const handleEmployerProvinceChange = (province: ProvinceOption | '' | null) => {
        const nextProvince = province && typeof province === 'object' ? province : null;

        setSelectedEmployerProvince(nextProvince);
        setSelectedEmployerCity(null);
        handleFieldChange('provinciaEmpleador', nextProvince?.nombre || '');
        handleFieldChange('idProvinciaEmpleador', nextProvince?.id || '');
        handleFieldChange('ciudadEmpleador', '');
        handleFieldChange('idLocalidadEmpleador', '');
        handleFieldChange('cpaEmpleador', '');
    };

    const handleEmployerCityChange = (city: CityOption | '' | null) => {
        const nextCity = city && typeof city === 'object' ? city : null;

        setSelectedEmployerCity(nextCity);
        handleFieldChange('idLocalidadEmpleador', nextCity?.id || '');
        handleFieldChange('ciudadEmpleador', nextCity?.nombre || '');
        handleFieldChange(
            'cpaEmpleador',
            nextCity?.codigoPostal ? String(nextCity.codigoPostal) : ''
        );
    };

    const handlePlaceProvinceChange = (province: ProvinceOption | '' | null) => {
        const nextProvince = province && typeof province === 'object' ? province : null;

        setSelectedPlaceProvince(nextProvince);
        setSelectedPlaceCity(null);
        handleFieldChange('provinciaLugar', nextProvince?.nombre || '');
        handleFieldChange('idProvinciaLugar', nextProvince?.id || '');
        handleFieldChange('ciudadLugar', '');
        handleFieldChange('idLocalidadLugar', '');
        handleFieldChange('cpaLugar', '');
    };

    const handlePlaceCityChange = (city: CityOption | '' | null) => {
        const nextCity = city && typeof city === 'object' ? city : null;

        setSelectedPlaceCity(nextCity);
        handleFieldChange('idLocalidadLugar', nextCity?.id || '');
        handleFieldChange('ciudadLugar', nextCity?.nombre || '');
        handleFieldChange('cpaLugar', nextCity?.codigoPostal ? String(nextCity.codigoPostal) : '');
    };

    const handleSave = async () => {
        const siniestroId = Number(siniestro?.id);

        if (!siniestroId) {
            enqueueSnackbar(t('detail.srtForm.alerts.claimMissing'), {
                variant: 'error'
            });
            return;
        }

        if (!validateSection(1)) {
            enqueueSnackbar(t('detail.srtForm.alerts.workerSectionRequired'), {
                variant: 'warning'
            });
            return;
        }

        if (!validateSection(2)) {
            enqueueSnackbar(t('detail.srtForm.alerts.employerSectionRequired'), {
                variant: 'warning'
            });
            return;
        }

        if (!validateSection(4)) {
            enqueueSnackbar(t('detail.srtForm.alerts.accidentPlaceSectionRequired'), {
                variant: 'warning'
            });
            return;
        }

        if (!validateSection(6)) {
            enqueueSnackbar(t('detail.srtForm.alerts.accidentFactsRequired'), {
                variant: 'warning'
            });
            return;
        }

        if (!validateSection(13)) {
            enqueueSnackbar(t('detail.srtForm.alerts.reportResponsibleSectionRequired'), {
                variant: 'warning'
            });
            return;
        }

        if (!validateSection(9)) {
            enqueueSnackbar(t('detail.srtForm.alerts.otherAffectedDocumentInvalid'), {
                variant: 'warning'
            });
            return;
        }

        if (!validateSection(10)) {
            enqueueSnackbar(t('detail.srtForm.alerts.causesMeasuresInvalid'), {
                variant: 'warning'
            });
            return;
        }

        try {
            const body = buildCreateSrtPayload(values, siniestro, srtParametricData);

            await createSrtFormulario({
                idSiniestro: siniestroId,
                body
            }).unwrap();

            enqueueSnackbar(t('detail.srtForm.alerts.saveSuccess'), {
                variant: 'success'
            });
        } catch (error: any) {
            enqueueSnackbar(getErrorMessage(error, t), {
                variant: 'error'
            });
        }
    };

    const handlePersistForm = async () => {
        const siniestroId = Number(siniestro?.id);

        if (!siniestroId && !overrides?.onSaveOverride) {
            enqueueSnackbar(t('detail.srtForm.alerts.claimMissing'), {
                variant: 'error'
            });
            return;
        }

        if (!validateSection(1)) {
            enqueueSnackbar(t('detail.srtForm.alerts.workerSectionRequired'), {
                variant: 'warning'
            });
            return;
        }

        if (!validateSection(2)) {
            enqueueSnackbar(t('detail.srtForm.alerts.employerSectionRequired'), {
                variant: 'warning'
            });
            return;
        }

        if (!validateSection(4)) {
            enqueueSnackbar(t('detail.srtForm.alerts.accidentPlaceSectionRequired'), {
                variant: 'warning'
            });
            return;
        }

        if (!validateSection(6)) {
            enqueueSnackbar(t('detail.srtForm.alerts.accidentFactsRequired'), {
                variant: 'warning'
            });
            return;
        }

        if (!validateSection(13)) {
            enqueueSnackbar(t('detail.srtForm.alerts.reportResponsibleSectionRequired'), {
                variant: 'warning'
            });
            return;
        }

        if (!validateSection(9)) {
            enqueueSnackbar(t('detail.srtForm.alerts.otherAffectedDocumentInvalid'), {
                variant: 'warning'
            });
            return;
        }

        if (!validateSection(10)) {
            enqueueSnackbar(t('detail.srtForm.alerts.causesMeasuresInvalid'), {
                variant: 'warning'
            });
            return;
        }

        try {
            const isFormComplete = esFormularioSrtCompleto(sectionsStatus, sectionsWithContent);
            const body = buildCreateSrtPayload(values, siniestro, srtParametricData);

            if (overrides?.onSaveOverride) {
                await overrides.onSaveOverride({ ...body, completado: isFormComplete });
                enqueueSnackbar(t('detail.srtForm.alerts.updateSuccess'), { variant: 'success' });
                return;
            }

            // Como en el flujo de visitas, el back recibe si quedó completo: en los siniestros
            // sin visitas lo pasa a Cargado o lo devuelve a Pendiente (SMP-610)
            const bodyBackOffice = { ...body, completado: isFormComplete };
            const savedForm = formularioId
                ? await patchSrtFormulario({
                      idSiniestro: siniestroId,
                      idFormulario: formularioId,
                      body: bodyBackOffice
                  }).unwrap()
                : await createSrtFormulario({
                      idSiniestro: siniestroId,
                      body: bodyBackOffice
                  }).unwrap();

            if (savedForm?.id) {
                setFormularioId(savedForm.id);
            }

            enqueueSnackbar(
                formularioId
                    ? t('detail.srtForm.alerts.updateSuccess')
                    : t('detail.srtForm.alerts.saveSuccess'),
                {
                    variant: 'success'
                }
            );
        } catch (error: any) {
            enqueueSnackbar(getErrorMessage(error, t), {
                variant: 'error'
            });
        }
    };

    const handleDiscardChanges = () => {
        resetLocationSelectors();

        if (srtFormularioData) {
            const mappedValues = Object.fromEntries(
                Object.entries(mapSrtFormularioToValues(srtFormularioData)).filter(
                    ([, value]) => value !== undefined
                )
            );

            resetForm(mappedValues);
        } else {
            resetForm();
        }

        hasInitializedOpenSectionsRef.current = false;
        setIsFormHydrated(true);
        enqueueSnackbar(t('detail.srtForm.alerts.discardSuccess'), {
            variant: 'info'
        });
    };

    return (
        <Box ref={formRef} sx={srtStyles.formContainer}>
            {isBootstrappingSrtFormulario ? (
                <Paper elevation={0} sx={srtStyles.sectionCard}>
                    <Box
                        sx={{
                            minHeight: 240,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <CircularProgress size={32} />
                    </Box>
                </Paper>
            ) : (
                <>
                    <Paper elevation={0} sx={srtStyles.headerCard}>
                        <Box sx={srtStyles.headerContent}>
                            <Box sx={srtStyles.headerIcon}>
                                <AssignmentOutlinedIcon fontSize="small" />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={srtStyles.headerTitle}>
                                    {t('detail.srtForm.title')}
                                </Typography>
                                <Typography variant="body2" sx={srtStyles.headerSubtitle}>
                                    {t('detail.srtForm.subtitle')}
                                </Typography>
                            </Box>
                        </Box>

                        {!overrides?.hideExport && (
                            <Box sx={srtStyles.exportActions}>
                                {!overrides?.ocultarTxt && (
                                    <Button
                                        type="contained"
                                        onClick={handleExportSrt}
                                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                                        style={srtStyles.exportButton as object}
                                        loading={isExportingSrt}
                                        disabled={!puedeExportarInvestigacion || isExportingSrt}
                                    >
                                        {t('detail.srtForm.actions.export')}
                                    </Button>
                                )}
                                {/* Un boton deshabilitado no recibe clics: el span los captura para avisar
                                    que falta completar el formulario. */}
                                <Box
                                    component="span"
                                    data-testid="descargar-pdf-investigacion"
                                    onClick={puedeDescargarPdf ? undefined : handleClickPdf}
                                >
                                    <Button
                                        type="contained"
                                        onClick={handleClickPdf}
                                        startIcon={<PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} />}
                                        style={srtStyles.exportButton as object}
                                        loading={descargandoPdf}
                                        disabled={!puedeDescargarPdf || descargandoPdf}
                                    >
                                        {t('detail.srtForm.actions.downloadPdf')}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Paper>

                    {hasBootstrapError && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {bootstrapErrorMessage}
                        </Alert>
                    )}

                    {!hasBootstrapError && (
                        <>
                            <Box sx={srtStyles.investigationHeader}>
                                <Typography variant="h5" sx={srtStyles.investigationTitle}>
                                    {t('detail.srtForm.investigationTitle')}
                                </Typography>
                                <Button
                                    type="outlined"
                                    onClick={handleToggleAllSections}
                                    style={srtStyles.collapseAllButton as object}
                                >
                                    {areAllSectionsCollapsed
                                        ? t('detail.srtForm.actions.expandAll')
                                        : t('detail.srtForm.actions.collapseAll')}
                                </Button>
                            </Box>

                            {sections.map((section, sectionIndex) => (
                                <FormSRTSectionCard
                                    key={section.id}
                                    section={section}
                                    displayNumber={sectionIndex + 1}
                                    isComplete={sectionsStatus[section.id]}
                                    showStatusChip={
                                        !OPTIONAL_REPEATABLE_SECTION_IDS.includes(section.id) ||
                                        sectionsWithContent[section.id]
                                    }
                                    isOpen={openSections[section.id]}
                                    onToggle={handleToggleSection}
                                    disabled={section.id === 11 && !hasMedidasCorrectivas}
                                >
                                    {openSections[section.id] && (
                                        <>
                                            {
                                        section.fields.map((field, fieldIndex) => {
                                if (isDividerField(field)) {
                                    return (
                                        <Box
                                            key={`${section.id}-divider-${fieldIndex}`}
                                            sx={srtStyles.internalDivider}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={srtStyles.internalDividerTitle}
                                            >
                                                {field.title}
                                            </Typography>
                                        </Box>
                                    );
                                }

                                if (
                                    section.id === 1 &&
                                    !isRepeatableField(field) &&
                                    WORKER_ADDRESS_FIELD_NAMES.has(field.name)
                                ) {
                                    return null;
                                }

                                if (
                                    section.id === 2 &&
                                    !isRepeatableField(field) &&
                                    EMPLOYER_ADDRESS_FIELD_NAMES.has(field.name)
                                ) {
                                    return null;
                                }

                                if (
                                    section.id === 4 &&
                                    !isRepeatableField(field) &&
                                    PLACE_ADDRESS_FIELD_NAMES.has(field.name)
                                ) {
                                    return null;
                                }

                                if (
                                    !isRepeatableField(field) &&
                                    CONDITIONAL_ONLY_FIELD_NAMES.has(field.name)
                                ) {
                                    return null;
                                }

                                if (isRepeatableField(field)) {
                                    return (
                                        <SrtRepeatableRenderer
                                            key={field.name}
                                            field={field}
                                            rows={values[field.name] || []}
                                            errors={errors as Record<string, string>}
                                            onChange={handleRepeatableFieldChange}
                                            onBlur={validateField}
                                            onAdd={addRepeatableRow}
                                            onRemove={removeRepeatableRow}
                                            canAdd={
                                                (values[field.name] || []).length <
                                                maxRepeatableRows
                                            }
                                            onNestedChange={handleMedidaFieldChange}
                                            onNestedBlur={(nestedField, value, rowIndex, nestedIndex) =>
                                                validateField(
                                                    nestedField,
                                                    value,
                                                    `${field.name}.${rowIndex}.medidas`,
                                                    nestedIndex
                                                )
                                            }
                                            onNestedAdd={addMedidaRow}
                                            onNestedRemove={removeMedidaRow}
                                            nestedMaxRows={maxRepeatableRows}
                                            disabled={overrides?.readOnly}
                                            isCellLoading={
                                                field.name === 'otrosAccidentados'
                                                    ? (rowIndex, fieldName) =>
                                                          (fieldName === 'apellidoNombres' ||
                                                              fieldName === 'nroDenuncia' ||
                                                              fieldName === 'patologiaTrazadora') &&
                                                          Boolean(
                                                              otherAffectedLookupLoading[rowIndex]
                                                          )
                                                    : undefined
                                            }
                                        />
                                    );
                                }

                                return (
                                    <Fragment key={field.name}>
                                        <SrtFieldRenderer
                                            field={field}
                                            value={values[field.name]}
                                            error={errors[field.name] as string}
                                            onChange={handleFieldChange}
                                            onBlur={validateField}
                                            disabled={
                                                overrides?.readOnly ||
                                                (overrides?.lockAutocompletedFields &&
                                                    AUTOCOMPLETED_LOCKABLE_FIELD_NAMES.has(
                                                        field.name
                                                    ) &&
                                                    hasValue(values[field.name]))
                                            }
                                        />
                                        {section.id === 1 &&
                                            field.name === 'nacionalidad' &&
                                            String(values.nacionalidad || '') ===
                                                OTHER_NATIONALITY_VALUE && (
                                                <SrtFieldRenderer
                                                    field={{
                                                        ...OTHER_NATIONALITY_FIELD,
                                                        label: t(
                                                            'detail.srtForm.fields.otrosNacionalidad'
                                                        )
                                                    }}
                                                    value={values.otrosNacionalidad}
                                                    error={errors.otrosNacionalidad as string}
                                                    onChange={handleFieldChange}
                                                    onBlur={validateField}
                                                    disabled={overrides?.readOnly}
                                                />
                                            )}
                                        {section.id === 4 &&
                                            field.name === 'grupoFiscalizacionCentro' &&
                                            String(values.grupoFiscalizacionCentro || '') ===
                                                OTHER_FISCALIZATION_VALUE && (
                                                <SrtFieldRenderer
                                                    field={{
                                                        name: 'otrosFiscalizacion',
                                                        label: t(
                                                            'detail.srtForm.fields.otrosFiscalizacion'
                                                        ),
                                                        type: 'text',
                                                        gridColumns: 4
                                                    }}
                                                    value={values.otrosFiscalizacion}
                                                    error={errors.otrosFiscalizacion as string}
                                                    onChange={handleFieldChange}
                                                    onBlur={validateField}
                                                    disabled={overrides?.readOnly}
                                                />
                                            )}
                                        {section.id === 4 && field.name === 'telefonoLugar' && (
                                            <>
                                                <Box sx={srtStyles.fieldGridColumn(4)}>
                                                    <AutocompleteComponent
                                                        loading={isLoadingProvinces}
                                                        value={selectedPlaceProvince}
                                                        onChange={handlePlaceProvinceChange}
                                                        name="provinciaLugar"
                                                        label={t('detail.srtForm.fields.provincia')}
                                                        options={provinceOptions}
                                                        fullWidth
                                                        size="small"
                                                        required
                                                        errors={errors}
                                                        showClearIcon
                                                        disabled={overrides?.readOnly}
                                                    />
                                                </Box>
                                                <Box sx={srtStyles.fieldGridColumn(4)}>
                                                    <AutocompleteComponent
                                                        loading={isLoadingPlaceCities}
                                                        value={selectedPlaceCity}
                                                        onChange={handlePlaceCityChange}
                                                        name="ciudadLugar"
                                                        label={t('detail.srtForm.fields.ciudad')}
                                                        options={placeCityOptions}
                                                        fullWidth
                                                        size="small"
                                                        required
                                                        errors={errors}
                                                        disabled={overrides?.readOnly || !selectedPlaceProvince}
                                                        showClearIcon
                                                    />
                                                </Box>
                                                <Box sx={srtStyles.fieldGridColumn(2)}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label={t(
                                                            'detail.srtForm.fields.codigoPostal'
                                                        )}
                                                        value={values.cpaLugar || ''}
                                                        required
                                                        error={Boolean(errors.cpaLugar)}
                                                        helperText={errors.cpaLugar as string}
                                                        disabled
                                                        sx={srtStyles.textField}
                                                    />
                                                </Box>
                                                <Box sx={srtStyles.fieldGridColumn(2)}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={t('detail.srtForm.fields.lugarCPA')}
                                                    value={values.lugarCPA || ''}
                                                    error={Boolean(errors.lugarCPA)}
                                                    helperText={renderFixedLengthHelper(
                                                        values.lugarCPA,
                                                        8,
                                                        errors.lugarCPA as string
                                                    )}
                                                    FormHelperTextProps={{
                                                        component: 'div'
                                                    }}
                                                    disabled={overrides?.readOnly}
                                                    onChange={(event) =>
                                                        handleFieldChange(
                                                            'lugarCPA',
                                                                event.target.value
                                                            )
                                                        }
                                                        inputProps={{ maxLength: 8 }}
                                                        sx={srtStyles.textField}
                                                    />
                                                </Box>
                                            </>
                                        )}
                                    </Fragment>
                                );
                                            })}
                            {section.id === 1 && (
                                <>
                                    <Box sx={srtStyles.fieldGridColumn(4)}>
                                        <AutocompleteComponent
                                            loading={isLoadingProvinces}
                                            value={selectedWorkerProvince}
                                            onChange={handleWorkerProvinceChange}
                                            name="provinciaTrabajador"
                                            label={t('detail.srtForm.fields.provincia')}
                                            options={provinceOptions}
                                            fullWidth
                                            size="small"
                                            required
                                            errors={errors}
                                            showClearIcon
                                            disabled={overrides?.readOnly}
                                        />
                                    </Box>
                                    <Box sx={srtStyles.fieldGridColumn(4)}>
                                        <AutocompleteComponent
                                            loading={isLoadingCities}
                                            value={selectedWorkerCity}
                                            onChange={handleWorkerCityChange}
                                            name="ciudadTrabajador"
                                            label={t('detail.srtForm.fields.ciudad')}
                                            options={workerCityOptions}
                                            fullWidth
                                            size="small"
                                            required
                                            errors={errors}
                                            disabled={overrides?.readOnly || !selectedWorkerProvince}
                                            showClearIcon
                                        />
                                    </Box>
                                    <Box sx={srtStyles.fieldGridColumn(4)}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={t('detail.srtForm.fields.codigoPostal')}
                                            value={values.codigoPostalTrabajador || ''}
                                            required
                                            error={Boolean(errors.codigoPostalTrabajador)}
                                            helperText={errors.codigoPostalTrabajador as string}
                                            disabled
                                            sx={srtStyles.textField}
                                        />
                                    </Box>
                                </>
                            )}
                            {section.id === 2 && (
                                <>
                                    <Box sx={srtStyles.fieldGridColumn(4)}>
                                        <AutocompleteComponent
                                            loading={isLoadingProvinces}
                                            value={selectedEmployerProvince}
                                            onChange={handleEmployerProvinceChange}
                                            name="idProvinciaEmpleador"
                                            label={t('detail.srtForm.fields.provincia')}
                                            options={provinceOptions}
                                            fullWidth
                                            size="small"
                                            showClearIcon
                                            disabled={
                                                overrides?.readOnly ||
                                                Boolean(
                                                    overrides?.lockAutocompletedFields &&
                                                        selectedEmployerProvince
                                                )
                                            }
                                        />
                                    </Box>
                                    <Box sx={srtStyles.fieldGridColumn(4)}>
                                        <AutocompleteComponent
                                            loading={isLoadingEmployerCities}
                                            value={selectedEmployerCity}
                                            onChange={handleEmployerCityChange}
                                            name="idLocalidadEmpleador"
                                            label={t('detail.srtForm.fields.ciudad')}
                                            options={employerCityOptions}
                                            fullWidth
                                            size="small"
                                            disabled={
                                                overrides?.readOnly ||
                                                !selectedEmployerProvince ||
                                                Boolean(
                                                    overrides?.lockAutocompletedFields &&
                                                        selectedEmployerCity
                                                )
                                            }
                                            showClearIcon
                                        />
                                    </Box>
                                    <Box sx={srtStyles.fieldGridColumn(4)}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={t('detail.srtForm.fields.codigoPostal')}
                                            value={values.cpaEmpleador || ''}
                                            disabled
                                            sx={srtStyles.textField}
                                        />
                                    </Box>
                                </>
                            )}
                            {section.id === 4 && isConstructionSiteSelected && (
                                <Box sx={srtStyles.highlightedFieldCard}>
                                    <Typography
                                        variant="body1"
                                        sx={srtStyles.highlightedFieldCardTitle}
                                    >
                                        {t('detail.srtForm.highlightedTitle')}
                                    </Typography>
                                    {constructionSiteFields.map((field) => (
                                        <SrtFieldRenderer
                                            key={field.name}
                                            field={field}
                                            value={values[field.name]}
                                            error={errors[field.name] as string}
                                            onChange={handleFieldChange}
                                            onBlur={validateField}
                                            disabled={overrides?.readOnly}
                                        />
                                    ))}
                                </Box>
                            )}
                                        </>
                                    )}
                        </FormSRTSectionCard>
                            ))}
                        </>
                    )}

                    <Paper elevation={0} sx={srtStyles.footerBar}>
                        <Box sx={srtStyles.footerActions}>
                            {!overrides?.readOnly && (
                                <Button
                                    type="outlined"
                                    style={srtStyles.footerButton as object}
                                    onClick={handleDiscardChanges}
                                    disabled={hasBootstrapError}
                                >
                                    {t('detail.srtForm.actions.discard')}
                                </Button>
                            )}
                            <Button
                                type="contained"
                                style={srtStyles.footerButton as object}
                                onClick={handlePersistForm}
                                loading={isSavingForm}
                                disabled={overrides?.readOnly || isSavingForm || hasBootstrapError}
                            >
                                {t('detail.srtForm.actions.saveProgress')}
                            </Button>
                            <IconButton sx={srtStyles.scrollTopButton} onClick={handleScrollToTop}>
                                <KeyboardArrowUpIcon />
                            </IconButton>
                        </Box>
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default FormSRT;
